import React from 'react';
import { useStyles } from '../enquiryFormDrawerStyles';
import { WIDE_LAYOUT_DRAWER_WIDTH, MEDIUM_LAYOUT_DRAWER_WIDTH } from '../enquiryFormDrawerConstants';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { HeaderWidgetSkeleton } from './HeaderWidgetSkeleton';
import { UserFeature, UserFeatureEnum } from '../../../types/UserFeature';

// Explicit request: while the drawer's own data (enquiryDetail/regionSettings/countryQuery/
// tenantQuery - the combined gate below replaces this with the real <Form>) is still loading,
// show a shell shaped like the real content instead of a bare centred spinner - specifically,
// if this is already known to be opening as the 3-column wide layout (isWideLayout is
// synchronous, derived from window.innerWidth + props.guid, independent of any of that data),
// show the 3-column shape from the first frame rather than only revealing it once loading
// finishes. Narrow viewports keep the old single-column shape.
//
// A sibling component here (not its own file) rather than importing useStyles/
// WIDE_LAYOUT_DRAWER_WIDTH from elsewhere - this calls the exact same classes the real content
// below uses (same module-level useStyles hook), so the shell is pixel-identical in size to
// what replaces it and nothing shifts when the real data arrives; a separate file would need to
// import those from this one, which this file would then need to import back - a circular
// import, and a fragile one given useStyles is used at module-eval time.
interface EnquiryFormDrawerSkeletonProps {
    isWideLayout: boolean;
    isMediumLayout: boolean;
    // Same offset-aware calc as the real content's own maxWidth below - passed through rather
    // than recomputed here, so the skeleton is pixel-identical to what replaces it instead of
    // flashing wider for a moment while a docked Quick View is eating into the available width.
    anchorOffset?: number;
    // The Next Follow-up / journey widgets only exist on an existing enquiry, so a new one has no placeholder for them.
    isCreateForm?: boolean;
}

export const EnquiryFormDrawerSkeleton: React.FC<EnquiryFormDrawerSkeletonProps> = ({ isWideLayout, isMediumLayout, anchorOffset, isCreateForm }) => {
    const classes = useStyles();

    return (
        <div
            className={classes.root}
            style={{
                maxWidth: isWideLayout
                    ? `min(${WIDE_LAYOUT_DRAWER_WIDTH}px, calc(100vw - ${anchorOffset ?? 0}px - 40px))`
                    : isMediumLayout
                        ? `min(${MEDIUM_LAYOUT_DRAWER_WIDTH}px, calc(100vw - ${anchorOffset ?? 0}px - 40px))`
                        : '700px',
            }}
        >
            <div className={classes.headerBar}>
                <SkeletonLoader
                    variant="shape"
                    shape="text"
                    width={160}
                    height={16}
                    style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                />
            </div>

            <div className={classes.infoPanel}>
                <div className={classes.infoPanelMain}>
                    <div className={classes.infoIdentity}>
                        <SkeletonLoader variant="shape" shape="circle" width={48} height={48} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* four lines, like the real identity block (name, organisation, email, phone), so the header does not grow when it arrives */}
                            <SkeletonLoader variant="text" rows={4} widths={['55%', '70%', '45%', '50%']} heights={[24, 16, 18, 18]} />
                        </div>
                    </div>
                    {isWideLayout && !isCreateForm && (
                        <div className={classes.journeyWidgetsCenteredGroup}>
                            <div className={classes.journeyNextFollowUpSlot}>
                                <HeaderWidgetSkeleton variant="nextFollowUp" />
                            </div>
                            <HeaderWidgetSkeleton variant="journey" />
                        </div>
                    )}
                    {isMediumLayout && !isCreateForm && (
                        <div className={classes.journeyNextFollowUpCenteredSlot}>
                            <HeaderWidgetSkeleton variant="nextFollowUp" />
                        </div>
                    )}
                    <div className={classes.headerRight}>
                        <SkeletonLoader variant="text" rows={2} widths={[90, 130]} />
                    </div>
                </div>
                {/* the real badge row only exists for a saved enquiry; the pills use the real infoBadge class around an 18px
                    inner bar (the rating stars' height) so each one is exactly as tall as the badge that replaces it */}
                {!isCreateForm && (
                    <div className={classes.badgeRow}>
                        <div className={classes.infoBadge}><SkeletonLoader variant="shape" shape="rect" width={146} height={18} style={{ borderRadius: 999 }} /></div>
                        <div className={classes.infoBadge}><SkeletonLoader variant="shape" shape="rect" width={162} height={18} style={{ borderRadius: 999 }} /></div>
                        <div className={classes.infoBadge}><SkeletonLoader variant="shape" shape="rect" width={110} height={18} style={{ borderRadius: 999 }} /></div>
                        {UserFeature.HasAccess(UserFeatureEnum.hasCRMEnquiryAssessmentAI) && (
                            <div className={classes.infoBadge}><SkeletonLoader variant="shape" shape="rect" width={40} height={18} style={{ borderRadius: 999 }} /></div>
                        )}
                    </div>
                )}
            </div>

            {isWideLayout ? (
                <div className={classes.tabsContentAreaWide}>
                    <div className={classes.columnHeadingBar} style={{ gridArea: 'detailsHead' }}>
                        <SkeletonLoader variant="shape" shape="text" width={64} height={18} />
                    </div>
                    <div className={classes.columnHeadingBar} style={{ gridArea: 'middleHead' }}>
                        <SkeletonLoader variant="shape" shape="text" width={64} height={18} />
                    </div>
                    <div className={classes.columnHeadingBar} style={{ gridArea: 'thirdHead' }}>
                        <SkeletonLoader variant="shape" shape="text" width={64} height={18} />
                    </div>
                    <div style={{ gridArea: 'details' }}>
                        <SkeletonLoader variant="form" rows={5} columns={1} />
                    </div>
                    <div style={{ gridArea: 'middle' }}>
                        {/* same row count as the loaded column's own skeleton (ActivityTabSection), so it does not change from 3 to 4 */}
                        <SkeletonLoader variant="timeline" rows={4} />
                    </div>
                    <div style={{ gridArea: 'third' }}>
                        <SkeletonLoader variant="panel" columns={2} rows={4} />
                    </div>
                </div>
            ) : isMediumLayout ? (
                <div className={classes.tabsContentAreaMedium}>
                    <div className={classes.columnHeadingBar} style={{ gridArea: 'leftHead' }}>
                        <SkeletonLoader variant="shape" shape="text" width={64} height={18} />
                    </div>
                    <div className={classes.columnHeadingBar} style={{ gridArea: 'thirdHead' }}>
                        <SkeletonLoader variant="shape" shape="text" width={64} height={18} />
                    </div>
                    <div style={{ gridArea: 'left' }}>
                        <SkeletonLoader variant="form" rows={5} columns={1} />
                    </div>
                    <div style={{ gridArea: 'third' }}>
                        <SkeletonLoader variant="panel" columns={2} rows={4} />
                    </div>
                </div>
            ) : (
                <div className={classes.tabsContentArea}>
                    <SkeletonLoader variant="form" rows={5} columns={2} />
                </div>
            )}
        </div>
    );
};
